<script>
import axios from '@/utils/axios';
import { viewPDF } from '@/utils/sales-order';
import Permissions from '@/utils/permissions.js';
import { paymentGateway } from '@/utils/app-store';
import { customerUtils } from '@/utils/customer.js';
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import SamplingStatusBadge from '@/components/common/sampling-status-badge.vue';
import SalesOrderStatusBadge from '@/components/common/sales-order-status-badge.vue';
import { createSiteInspection } from '@/utils/site-inspection'


export default {
 inject: ['setLoading'],  
 components: {
  SamplingStatusBadge,
  SalesOrderStatusBadge
 },
 data() {
  return {
   editor: ClassicEditor,
   editorData: " ",
   editorConfig: {},
   errors: {},
   customer: [],
   sources: [],
   samplings: [],
   subscriptions: [],
   assignment_history: [],
   payments: [],
   pagination: {
    current_page: 1,
    per_page: 10,
    last_page: 1,
    total: 0,
    links: []
   },
   razorpay_status: '',
   client_type: 'App\\Models\\Customer',
   permissions: Permissions,
  }
 },

 computed: {
  serialNo(){
   return (this.pagination.current_page - 1) * this.pagination.per_page;
  },
 },

 async mounted(){
  const customer_id = this.$route.params.id;
  this.customerById(customer_id);
  this.getSources();
  await Permissions.init();
  this.paymentGatewayStatus();
 },

 methods: {

  async createSiteInspection() {
    this.setLoading(true)
    try {
        const res = await createSiteInspection({
            party_type: 'App\\Models\\Customer',
            party_id: this.customer.id
        })
        this.$swalToast(res.data.message)
    } catch (error) {
        if (error.type === 'validation') {
            const firstError = Object.values(error.errors)[0]
            this.$swalToast(firstError[0], 'error')
        } else {
            this.$swalToast(error.message, 'error')
        }
    } finally {
        this.setLoading(false)
    }
},


  async paymentGatewayStatus(){
   try{
    const data = await paymentGateway();
    this.razorpay_status = data.Razorpay;
   }catch(error){
    this.$swalToast(error.message, 'error');
   } 
  },

  async getSources() {
   this.setLoading(true);
   try{
     const response = await axios.get('/lead-sources');
     this.sources = response.data.data;
    }catch(error){
     this.$swalToast(error.message, 'error');
    }finally{
     this.setLoading(false);
    }
  },

  async customerById(customer_id){
   this.customer = await customerUtils.getCustomerById(customer_id);
   if(this.customer === null){
    this.$router.push('/crm/customers');
   }
  },

  async loadAssignmentHistory(){
   this.setLoading(true);
   try{
    const response = await axios.get(`/customers/${this.customer.id}/assignment/history`);  
    this.assignment_history = response.data.data;
   }catch(error){
    this.$swalToast(error.message, 'error');
   }finally{
    this.setLoading(false);
   } 
  },

  async loadSubscriptions(){
   this.setLoading(true);
   try{
    const response = await axios.get(`/subscriptions/${this.customer.id}/customer`);
    this.subscriptions = response.data.data;
   }catch(error){
    this.$swalToast(error.message, 'error');
   }finally{
    this.setLoading(false);
   }
  },

  async loadPayments(page = 1){
   this.setLoading(true);
   try{
    const params = {
     page,
     per_page: this.pagination.per_page
    };
    const query_string = new URLSearchParams(params).toString();
    const response = await axios.get(`/customers/${this.customer.id}/payments?${query_string}`);
    this.payments = response.data.data.data;
    this.pagination = {
     current_page: response.data.data.current_page,
     per_page: response.data.data.per_page,
     last_page: response.data.data.last_page,
     total: response.data.data.total,
     links: response.data.data.links
    };
   }catch(error){
    this.$swalToast(error.message, 'error');
   }finally{
    this.setLoading(false);
   }
  },

  async salesOrderPDF(id){
   try{
    const response = await viewPDF(id);
    const file = new Blob([response.data], { type: 'application/pdf' });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, '_blank');
   }catch(error){
    this.$swalToast("Failed to open PDF", 'error');
    console.error(error);
   }
  },

  changePage(url) {
   if (!url) return;
   const page = new URL(url).searchParams.get("page");
   this.loadPayments(page);
  },

 },
}
</script>

<template>
<layout-header></layout-header>
<layout-sidebar></layout-sidebar>
 
<div class="page-wrapper cd-page-wrapper">
  <div class="cd-container content">
 
    <!-- Top Bar -->
    <div class="cd-topbar">
      <div class="cd-topbar-left">
        <router-link to="/crm/customers" class="cd-back-btn">
          <i class="ti ti-arrow-left"></i>
          <span>Back to customers</span>
        </router-link>
        <span class="cd-breadcrumb-sep">/</span>
        <span class="cd-breadcrumb-current">Customer Details</span>
      </div>
    </div>
 
    <!-- Main Content -->
    <div class="cd-body">
 
      <!-- LEFT PANEL -->
      <div class="cd-left-panel">
 
        <!-- Profile Card -->
        <div class="cd-card cd-profile-card">
          <div class="cd-avatar-wrap">
            <span class="cd-avatar">
              <img src="@/assets/img/profiles/avatar-12.jpg" />
            </span>
          </div>
          <h4 class="cd-lead-name">{{ customer.salutation }} {{ customer.name }}</h4>
          <p class="cd-lead-company" v-if="customer.company_name">
            <i class="ti ti-building me-1"></i>{{ customer.company_name }}
          </p>
          <p class="cd-lead-location" v-if="customer.city || customer.country">
            <i class="ti ti-map-pin me-1"></i>
            {{ [customer.city, customer.state, customer.country].filter(Boolean).join(', ') }}
            <span v-if="customer.pincode"> - {{ customer.pincode }}</span>
          </p>
 
          <!-- Quick Action Buttons -->
          <div class="cd-quick-actions">
            <div class="dropdown">
              <button type="button" class="cd-create-btn dropdown-toggle" data-bs-toggle="dropdown">
                <i class="ti ti-plus me-1"></i> Create New
              </button>
              <ul class="dropdown-menu">
                <li v-if="permissions.isLoaded && permissions.canCreateQuotation">
                  <a class="dropdown-item" href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#quotation_modal" @click="$refs.quotationModal.createViaParty(client_type, customer.id)">
                    <i class="ti ti-file-check me-2 text-success"></i>Quotation
                  </a>
                </li>
                <li v-if="permissions.isLoaded && permissions.canCreateProforma">
                  <a class="dropdown-item" href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#proforma_invoice_modal" @click="$refs.proformaInvoiceModal.createViaParty(client_type, customer.id)">
                    <i class="ti ti-receipt-rupee me-2 text-warning"></i>Proforma Invoice
                  </a>
                </li>
                <li><hr class="dropdown-divider"></li>
                <li v-if="razorpay_status === 'Connected'">
                  <a class="dropdown-item" href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#payment_link_modal" @click="$refs.paymentLinkModal.createViaParty(client_type, customer.id)">
                    <i class="ti ti-link me-2 text-info"></i>Payment Link
                  </a>
                </li>
                <li v-if="permissions.isLoaded && permissions.canCreateSampling">
                  <a class="dropdown-item" href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#sampling_modal" @click="$refs.samplingModal.createViaParty(client_type, customer.id)">
                    <i class="ti ti-cube-send me-2 text-secondary"></i>Sampling Request
                  </a>
                </li>
                <li v-if="permissions.isLoaded && permissions.canCreateSalesOrder">
                  <a class="dropdown-item" href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#sales_order_modal" @click="$refs.salesOrderModal.createViaParty(client_type, customer.id)">
                    <i class="ti ti-shopping-cart me-2 text-danger"></i>Sales Order
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="javascript:void(0);" @click="createSiteInspection">
                      <i class="ti ti-clipboard-check me-2 text-teal"></i>Site Inspection
                  </a>
              </li>
              </ul>
            </div>
          </div>
        </div>
 
        <!-- Customer Information Card -->
        <div class="cd-card">
          <div class="cd-card-header">
            <span class="cd-section-title">Customer Information</span>
            <a href="javascript:void(0);" class="cd-edit-btn" data-bs-toggle="modal" data-bs-target="#customer_modal" @click="$refs.customerModal.edit(customer.id)">
              <i class="ti ti-edit"></i>
            </a>
          </div>
          <div class="cd-info-list">
            <div class="cd-info-row" v-if="customer.email">
              <span class="cd-info-label"><i class="ti ti-mail"></i> Email</span>
              <span class="cd-info-value">{{ customer.email }}</span>
            </div>
            <div class="cd-info-row">
              <span class="cd-info-label"><i class="ti ti-phone"></i> Phone</span>
              <span class="cd-info-value">{{ customer.mobile }}</span>
            </div>
            <div class="cd-info-row">
              <span class="cd-info-label"><i class="ti ti-phone-plus"></i> Alt. Phone</span>
              <span class="cd-info-value">{{ customer.phone || 'NA' }}</span>
            </div>
            <div class="cd-info-row">
              <span class="cd-info-label"><i class="ti ti-database-share"></i> Source</span>
              <span class="cd-info-value">
                <span class="cd-badge cd-badge-green">{{ customer.source?.name }}</span>
              </span>
            </div>
            <div class="cd-info-row" v-if="customer.designation">
              <span class="cd-info-label"><i class="ti ti-user-star"></i> Designation</span>
              <span class="cd-info-value">{{ customer?.designation }}</span>
            </div>
            <div class="cd-info-row" v-if="customer.website">
              <span class="cd-info-label"><i class="ti ti-world-www"></i> Website</span>
              <span class="cd-info-value">
                <a :href="customer?.website" target="_blank" class="cd-link-primary">{{ customer?.website }}</a>
              </span>
            </div>
            <div class="cd-info-row">
              <span class="cd-info-label"><i class="ti ti-calendar"></i> Created</span>
              <span class="cd-info-value">{{ $formatDate(customer.created_at, 'DD MMM YYYY, hh:mm a') }}</span>
            </div>
          </div>
        </div>
 
        <!-- Assigned Card -->
        <div class="cd-card">
          <div class="cd-card-header">
            <span class="cd-section-title">Assigned To</span>
            <a href="javascript:void(0);" class="cd-edit-btn" data-bs-toggle="modal" data-bs-target="#customer_assign_modal" @click="$refs.customerModal.assignForm(customer.id)">
              <i class="ti ti-edit"></i>
            </a>
          </div>
          <div class="cd-assigned-row">
          <span class="cd-user-avatar">
            <img v-if="customer.user?.image" :src="customer.user?.image_url" width="32" />
            <img v-else src="@/assets/img/profiles/avatar-12.jpg" width="32" />
          </span>
            <div>
              <p class="cd-user-name">{{ customer.user?.name }}</p>
              <p class="cd-user-role">Customer Owner</p>
            </div>
          </div>
        </div>
 
        <!-- Other Information Card -->
        <div class="cd-card">
          <div class="cd-card-header">
            <span class="cd-section-title">Other Information</span>
          </div>
          <div class="cd-info-list">
            <div class="cd-info-row">
              <span class="cd-info-label"><i class="ti ti-user-check"></i> Created By</span>
              <span class="cd-info-value cd-flex-center">
                <span class="cd-user-avatar cd-user-avatar-sm me-1">
                  <img v-if="customer.created_by?.image" :src="customer.created_by?.image_url" width="20" />
                  <img v-else src="@/assets/img/profiles/avatar-12.jpg" width="20" />
                </span>
                {{ customer.created_by?.name }}
              </span>
            </div>
            <div class="cd-info-row">
              <span class="cd-info-label"><i class="ti ti-calendar-check"></i> Last Modified</span>
              <span class="cd-info-value">{{ $formatDate(customer.updated_at, 'DD MMM YYYY, hh:mm a') }}</span>
            </div>
          </div>
        </div>
 
      </div>
      <!-- /LEFT PANEL -->
 
      <!-- RIGHT PANEL -->
      <div class="cd-right-panel">
 
        <!-- Tabs -->
        <div class="cd-tabs-card">
          <ul class="cd-tabs" role="tablist">
            <li role="presentation">
              <a class="cd-tab active" href="#bottom-justified-tab2" data-bs-toggle="tab" role="tab">
                <i class="ti ti-file-description"></i> Notes
              </a>
            </li>
            <li role="presentation">
              <a class="cd-tab" href="#assignment-history-tab" data-bs-toggle="tab" role="tab" @click="loadAssignmentHistory">
                <i class="ti ti-history"></i> Assign History
              </a>
            </li>
            <li role="presentation">
              <a class="cd-tab" href="#subscription-tab" data-bs-toggle="tab" role="tab" @click="loadSubscriptions">
                <i class="ti ti-repeat"></i> Subscriptions
              </a>
            </li>
            <li role="presentation">
              <a class="cd-tab" href="#payments-tab" data-bs-toggle="tab" role="tab" @click="loadPayments(1)">
                <i class="ti ti-receipt-rupee"></i> Payments
              </a>
            </li>
            <li role="presentation" v-if="permissions.isLoaded && permissions.canCreateSampling">
              <a class="cd-tab" href="#sampling-tab" data-bs-toggle="tab" role="tab">
                <i class="ti ti-cube-send"></i> Sampling
              </a>
            </li>
            <li role="presentation" v-if="permissions.isLoaded && permissions.canCreateSalesOrder">
              <a class="cd-tab" href="#sales-order-tab" data-bs-toggle="tab" role="tab">
                <i class="ti ti-shopping-cart"></i> Sales Order
              </a>
            </li>
          </ul>
        </div>
 
        <!-- Tab Content -->
        <div class="tab-content">
 
          <!-- Notes -->
          <div class="tab-pane active show" id="bottom-justified-tab2" role="tabpanel">
            <div class="cd-card cd-tab-content-card">
              <div class="cd-tab-header">
                <h6 class="cd-tab-title"><i class="ti ti-file-description me-2"></i>Notes</h6>
                <a href="javascript:void(0);" class="cd-add-btn" data-bs-toggle="modal" data-bs-target="#customer_notes_modal" @click="$refs.customerNotesModal.addCustomerNote(customer);">
                  <i class="ti ti-circle-plus me-1"></i> Add Note
                </a>
              </div>
              <div class="cd-tab-body">
                <template v-if="customer.comments?.length">
                  <div class="cd-note-item" v-for="row in customer.comments">
                    <div class="cd-note-header">
                      <div class="cd-flex-center">
                        <span class="cd-user-avatar cd-user-avatar-sm me-2">
                        <img v-if="row.user?.image" :src="row.user?.image_url" width="28" />
                        <img v-else src="@/assets/img/profiles/avatar-12.jpg" width="28" />
                      </span>
                        <span class="cd-note-author">{{ row.user?.name }}</span>
                      </div>
                      <span class="cd-note-time"><i class="ti ti-clock-hour-9 me-1"></i>{{ $formatDate(row.created_at, 'DD MMM YYYY, hh:mm a') }}</span>
                    </div>
                    <div class="cd-note-body" v-html="row.description"></div>
                  </div>
                </template>
                <div v-else class="cd-empty-state">
                  <i class="ti ti-notes"></i>
                  <p>No notes added yet</p>
                </div>
              </div>
            </div>
          </div>
 
          <!-- Assignment History -->
          <div class="tab-pane" id="assignment-history-tab" role="tabpanel">
            <div class="cd-card cd-tab-content-card">
              <div class="cd-tab-header">
                <h6 class="cd-tab-title"><i class="ti ti-history me-2"></i>Assignment History</h6>
              </div>
              <div class="cd-tab-body cd-tab-body-table">
                <div class="cd-table-wrap" v-if="assignment_history.length">
                  <table class="cd-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Assigned By</th>
                        <th>Assigned From</th>
                        <th>Assigned To</th>
                        <th>Remark</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in assignment_history" :key="row.id">
                        <td>{{ index + 1 }}</td>
                        <td>{{ row.assign_by.name }}</td>
                        <td>{{ row.assign_from.name }}</td>
                        <td>{{ row.assign_to.name }}</td>
                        <td>{{ row.remark }}</td>
                        <td>{{ $formatDate(row.created_at, 'DD-MM-YY, hh:mm a') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="cd-empty-state">
                  <i class="ti ti-history"></i>
                  <p>No history found</p>
                </div>
              </div>
            </div>
          </div>
 
          <!-- Subscriptions -->
          <div class="tab-pane" id="subscription-tab" role="tabpanel">
            <div class="cd-card cd-tab-content-card">
              <div class="cd-tab-header">
                <h6 class="cd-tab-title"><i class="ti ti-repeat me-2"></i>Subscriptions</h6>
                <a href="javascript:void(0);" class="cd-add-btn" data-bs-toggle="modal" data-bs-target="#subscription_modal" @click="$refs.subscriptionModal.add(customer.id);">
                  <i class="ti ti-circle-plus me-1"></i> Add New
                </a>
              </div>
              <div class="cd-tab-body cd-tab-body-table">
                <div class="cd-table-wrap" v-if="subscriptions.length">
                  <table class="cd-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Duration</th>
                        <th>Status</th>
                        <th>End Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in subscriptions" :key="row.id">
                        <td>{{ index + 1 }}</td>
                        <td>
                          <a href="javascript:void(0);" class="cd-link-primary" data-bs-toggle="modal" data-bs-target="#subscription_view_modal" @click="$refs.subscriptionModal.edit(row);">
                            {{ row.subscription_name }}
                          </a>
                          <span v-if="$currentDate() > row.end_date" class="cd-badge cd-badge-red ms-2">Expired</span>
                        </td>
                        <td>{{ row.duration }} {{ row.duration_type }}</td>
                        <td>
                          <span :class="['cd-badge', {
                            'cd-badge-green'  : row.status === 'active',
                            'cd-badge-dark'   : row.status === 'paused',
                            'cd-badge-blue'   : row.status === 'completed',
                            'cd-badge-red'    : row.status === 'cancelled'
                          }]">{{ row.status }}</span>
                        </td>
                        <td>{{ $formatDate(row.end_date, 'DD-MM-YYYY') }}</td>
                        <td>
                          <div class="dropdown">
                            <button class="cd-dots-btn" type="button" data-bs-toggle="dropdown">
                              <i class="ti ti-dots-vertical"></i>
                            </button>
                            <ul class="dropdown-menu dropdown-menu-end p-2">
                              <li>
                                <a class="dropdown-item rounded-1" href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#subscription_modal" @click="$refs.subscriptionModal.edit(row)">
                                  <i class="ti ti-edit me-1"></i>Edit
                                </a>
                              </li>
                              <li>
                                <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#subscription_delete_modal" @click="$refs.subscriptionModal.edit(row)">
                                  <i class="ti ti-trash me-1"></i>Delete
                                </a>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="cd-empty-state">
                  <i class="ti ti-repeat"></i>
                  <p>No subscriptions found</p>
                </div>
              </div>
            </div>
          </div>
 
          <!-- Payments -->
          <div class="tab-pane" id="payments-tab" role="tabpanel">
            <div class="cd-card cd-tab-content-card">
              <div class="cd-tab-header">
                <h6 class="cd-tab-title"><i class="ti ti-receipt-rupee me-2"></i>Payment History</h6>
                <a href="javascript:void(0);" class="cd-add-btn" data-bs-toggle="modal" data-bs-target="#payment_add_modal" @click="$refs.paymentAddModal.create(customer.id)">
                  <i class="ti ti-circle-plus me-1"></i> Add Payment
                </a>
              </div>
              <div class="cd-tab-body cd-tab-body-table">
                <div class="cd-table-wrap" v-if="payments.length">
                  <table class="cd-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Received By</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in payments" :key="row.id">
                        <td>{{ serialNo + index + 1 }}</td>
                        <td>
                          <a href="javascript:void(0);" class="cd-link-primary fw-bold" data-bs-toggle="modal" data-bs-target="#payment_detail_modal" @click="$refs.paymentModal.detail(row.id, row.transaction_id, row.notes);">
                            {{ row.amount }}
                          </a>
                        </td>
                        <td>{{ row.payment_method }}</td>
                        <td>
                          <div class="cd-flex-center" style="gap:6px">
                            <img v-if="row.user?.image" :src="row.user?.image_url" width="22" style="border-radius:50%" />
                            <img v-else src="@/assets/img/profiles/avatar-12.jpg" width="22" style="border-radius:50%" />
                            {{ row.user.name }}
                          </div>
                        </td>
                        <td>{{ $formatDate(row.created_at, 'DD-MM-YY, hh:mm a') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="cd-empty-state">
                  <i class="ti ti-receipt"></i>
                  <p>No payment records found</p>
                </div>
              </div>
              <!-- Pagination -->
              <div class="cd-pagination-wrap" v-if="pagination.links && pagination.links.length > 3">
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li v-for="(link, index) in pagination.links" :key="index"
                      :class="['page-item', { active: link.active, disabled: !link.url }]">
                      <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
 
          <!-- Sampling -->
          <div class="tab-pane" id="sampling-tab" role="tabpanel">
            <div class="cd-card cd-tab-content-card">
              <div class="cd-tab-header">
                <h6 class="cd-tab-title"><i class="ti ti-cube-send me-2"></i>Sampling Requests</h6>
              </div>
              <div class="cd-tab-body cd-tab-body-table">
                <div class="cd-table-wrap" v-if="customer?.samplings?.length">
                  <table class="cd-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Request No</th>
                        <th>Status</th>
                        <th>Assigned To</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in customer?.samplings" :key="row.id">
                        <td>{{ index + 1 }}</td>
                        <td>
                          <a href="javascript:void(0);" class="cd-link-primary fw-bold" data-bs-toggle="modal" data-bs-target="#sampling_detail_modal" @click="$refs.samplingModal.sampling(row);">
                            {{ row.request_number }}
                          </a>
                        </td>
                        <td><SamplingStatusBadge :status="row.status"></SamplingStatusBadge></td>
                        <td>
                          <div class="cd-flex-center" style="gap:6px">
                            <img v-if="row.user?.image" :src="row.user?.image_url" width="22" style="border-radius:50%" />
                            <img v-else src="@/assets/img/profiles/avatar-12.jpg" width="22" style="border-radius:50%" />
                            {{ row.user.name }}
                          </div>
                        </td>
                        <td>{{ $formatDate(row.created_at, 'DD-MM-YY, hh:mm a') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="cd-empty-state">
                  <i class="ti ti-cube-off"></i>
                  <p>No sampling requests found</p>
                </div>
              </div>
            </div>
          </div>
 
          <!-- Sales Orders -->
          <div class="tab-pane" id="sales-order-tab" role="tabpanel">
            <div class="cd-card cd-tab-content-card">
              <div class="cd-tab-header">
                <h6 class="cd-tab-title"><i class="ti ti-shopping-cart me-2"></i>Sales Orders</h6>
              </div>
              <div class="cd-tab-body cd-tab-body-table">
                <div class="cd-table-wrap" v-if="customer?.sales_orders?.length">
                  <table class="cd-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>SO No</th>
                        <th>Status</th>
                        <th>Assigned To</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in customer?.sales_orders" :key="row.id">
                        <td>{{ index + 1 }}</td>
                        <td>
                          <a href="javascript:void(0);" class="cd-link-primary fw-bold" data-bs-toggle="modal" data-bs-target="#sales_order_detail_modal" @click="$refs.salesOrderModal.salesOrderActivity(row);">
                            {{ row.so_number }}
                          </a>
                        </td>
                        <td><SalesOrderStatusBadge :status="row.status"></SalesOrderStatusBadge></td>
                        <td>
                          <div class="cd-flex-center" style="gap:6px">
                            <img v-if="row.user?.image" :src="row.user?.image_url" width="22" style="border-radius:50%" />
                            <img v-else src="@/assets/img/profiles/avatar-12.jpg" width="22" style="border-radius:50%" />
                            {{ row.user?.name }}
                          </div>
                        </td>
                        <td>{{ $formatDate(row.created_at, 'DD-MM-YY, hh:mm a') }}</td>
                        <td>
                          <div class="dropdown">
                            <button class="cd-dots-btn" type="button" data-bs-toggle="dropdown">
                              <i class="ti ti-dots-vertical"></i>
                            </button>
                            <ul class="dropdown-menu dropdown-menu-end p-2">
                              <li>
                                <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="salesOrderPDF(row.id)">
                                  <i class="ti ti-file-type-pdf me-1"></i>View PDF
                                </a>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="cd-empty-state">
                  <i class="ti ti-shopping-cart-off"></i>
                  <p>No sales orders found</p>
                </div>
              </div>
            </div>
          </div>
 
        </div>
        <!-- /Tab Content -->
      </div>
      <!-- /RIGHT PANEL -->
 
    </div>
  </div>
  <app-footer></app-footer>
</div>
 
<!-- Modals -->
<customers-modal ref="customerModal" @updated="customerById(customer.id);"></customers-modal>
<customers-notes-modal ref="customerNotesModal" @updated="customerById(customer.id);"></customers-notes-modal>
 
<payment-modal ref="paymentModal"></payment-modal>
<payment-add-modal ref="paymentAddModal" @updated="loadPayments(1)"></payment-add-modal>
<quotation-modal ref="quotationModal"></quotation-modal>
<payment-link-modal ref="paymentLinkModal"></payment-link-modal>
<proforma-invoice-modal ref="proformaInvoiceModal"></proforma-invoice-modal>
<sampling-modal ref="samplingModal" @updated="customerById(customer.id)"></sampling-modal>
<subscription-modal ref="subscriptionModal" @updated="loadSubscriptions"></subscription-modal>
<sales-order-modal ref="salesOrderModal" @updated="customerById(customer.id)"></sales-order-modal>
 
</template>
 
<style scoped>
/* ============================================================
   CUSTOMER DETAILS 
   ============================================================ */
 
.cd-page-wrapper {
  /* inherits from global page-wrapper */
}
 
/* ---- TOP BAR ---- */
.cd-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}
 
.cd-topbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
 
.cd-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-decoration: none;
  padding: 6px 12px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  transition: all 0.15s;
}
.cd-back-btn:hover { background: #f9fafb; color: #111827; }
 
.cd-breadcrumb-sep { color: #9ca3af; font-size: 13px; }
.cd-breadcrumb-current { font-size: 13px; color: #6b7280; }
 
/* ---- BODY LAYOUT ---- */
.cd-body {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 20px;
  align-items: start;
}
 
/* ---- CARDS ---- */
.cd-card {
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  margin-bottom: 16px;
  overflow: visible; /* allow dropdowns to escape */
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
 
/* Tab content cards: re-clip so table scroll works */
.cd-tab-content-card {
  overflow: hidden;
}
 
.cd-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px 10px;
  border-bottom: 1px solid #f3f4f6;
}
 
.cd-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #6b7280;
}
 
.cd-edit-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  font-size: 13px;
  transition: all 0.15s;
}
.cd-edit-btn:hover { background: #f3f4f6; color: #374151; }
 
/* ---- PROFILE CARD ---- */
.cd-profile-card {
  text-align: center;
  padding: 28px 20px 20px;
}
 
.cd-avatar-wrap {
  position: relative;
  display: inline-block;
  margin-bottom: 14px;
}
.cd-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #fff;
  box-shadow: 0 2px 10px rgba(0,0,0,0.12);
  display: block;
}
.cd-avatar img { width: 100%; height: 100%; object-fit: cover; }
 
.cd-lead-name { font-size: 16px; font-weight: 700; color: #111827; margin-bottom: 4px; }
.cd-lead-company { font-size: 13px; color: #6b7280; margin-bottom: 4px; }
.cd-lead-location { font-size: 12px; color: #9ca3af; margin-bottom: 0; }
 
/* ---- CREATE NEW ---- */
.cd-quick-actions {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
  position: relative;
  z-index: 100;
}
 
.cd-create-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 20px;
  border-radius: 8px;
  background: #f97316;
  color: #fff !important;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}
.cd-create-btn:hover { background: #ea6c0a; color: #fff !important; }
 
.cd-quick-actions .dropdown-menu {
  z-index: 9999 !important;
  min-width: 200px;
  font-size: 13px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  padding: 6px;
}
.cd-quick-actions .dropdown-item {
  border-radius: 6px;
  padding: 7px 12px;
  font-size: 13px;
}
 
/* ---- INFO LIST ---- */
.cd-info-list { padding: 8px 0; }
 
.cd-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 18px;
  gap: 8px;
  border-bottom: 1px solid #f9fafb;
}
.cd-info-row:last-child { border-bottom: none; }
 
.cd-info-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
  min-width: 100px;
  white-space: nowrap;
}
.cd-info-label i { font-size: 13px; }
 
.cd-info-value {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  text-align: right;
  word-break: break-all;
}
 
/* ---- BADGES ---- */
.cd-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: capitalize;
}
.cd-badge-green  { background: #dcfce7; color: #15803d; }
.cd-badge-blue   { background: #dbeafe; color: #1d4ed8; }
.cd-badge-pink   { background: #fce7f3; color: #be185d; }
.cd-badge-yellow { background: #fef9c3; color: #854d0e; }
.cd-badge-dark   { background: #f3f4f6; color: #374151; }
.cd-badge-red    { background: #fee2e2; color: #b91c1c; }
 
/* ---- ASSIGNED ROW ---- */
.cd-assigned-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
}
 
.cd-user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  flex-shrink: 0;
}
.cd-user-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.cd-user-avatar-sm { width: 26px; height: 26px; }
 
.cd-user-name { font-size: 13px; font-weight: 600; color: #111827; margin: 0; }
.cd-user-role  { font-size: 11px; color: #9ca3af; margin: 0; }
 
/* ---- TABS ---- */
.cd-tabs-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 4px 8px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  display: flex;
  align-items: center;
  overflow-x: auto;
}
 
.cd-tabs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: nowrap;
  min-width: max-content;
}
 
.cd-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  transition: all 0.15s;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
}
.cd-tab:hover { color: #374151; background: #f9fafb; }
.cd-tab.active,
.cd-tab[aria-selected="true"] {
  color: #f97316;
  font-weight: 600;
  border-bottom-color: #f97316;
  background: #fff7ed;
}
.cd-tab i { font-size: 14px; }
 
/* ---- TAB CONTENT ---- */
.cd-tab-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f3f4f6;
  flex-wrap: wrap;
  gap: 10px;
}
 
.cd-tab-title { font-size: 14px; font-weight: 600; color: #111827; margin: 0; }
 
/* Normal tab body */
.cd-tab-body { padding: 16px 20px; }
 
/* Table tab body — no padding for proper horizontal scroll */
.cd-tab-body-table { padding: 0; }
 
.cd-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #f97316;
  text-decoration: none;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid #fed7aa;
  background: #fff7ed;
  transition: all 0.15s;
}
.cd-add-btn:hover { background: #ffedd5; color: #ea6c0a; }
 
/* ---- NOTES ---- */
.cd-note-item {
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
  background: #fafafa;
}
.cd-note-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 6px;
}
.cd-note-author { font-size: 13px; font-weight: 600; color: #111827; }
.cd-note-time { font-size: 11px; color: #9ca3af; display: flex; align-items: center; }
.cd-note-body { font-size: 13px; color: #374151; line-height: 1.6; }
 
/* ---- TABLE ---- */
.cd-table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  display: block;
}
 
.cd-table {
  width: 100%;
  min-width: 540px;
  border-collapse: collapse;
  font-size: 13px;
}
 
.cd-table thead tr { background: #f9fafb; }
 
.cd-table th {
  padding: 10px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  border-bottom: 1px solid #f3f4f6;
  white-space: nowrap;
}
 
.cd-table td {
  padding: 12px 16px;
  color: #374151;
  border-bottom: 1px solid #f9fafb;
  vertical-align: middle;
  white-space: nowrap;
}
 
.cd-table tbody tr:last-child td { border-bottom: none; }
.cd-table tbody tr:hover { background: #fafafa; }
 
.cd-link-primary { color: #f97316; font-weight: 600; text-decoration: none; }
.cd-link-primary:hover { text-decoration: underline; }
 
/* ---- DOTS BTN ---- */
.cd-dots-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #9ca3af;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}
.cd-dots-btn:hover { background: #f3f4f6; color: #374151; }
 
/* ---- PAGINATION ---- */
.cd-pagination-wrap {
  padding: 12px 16px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: flex-end;
}
 
/* ---- EMPTY STATE ---- */
.cd-empty-state {
  text-align: center;
  padding: 40px 20px;
}
.cd-empty-state i { font-size: 36px; display: block; margin-bottom: 8px; color: #d1d5db; }
.cd-empty-state p  { font-size: 13px; color: #9ca3af; margin: 0; }
 
/* ---- UTILS ---- */
.cd-flex-center { display: flex; align-items: center; }
.me-1 { margin-right: 4px; }
.me-2 { margin-right: 8px; }
.ms-2 { margin-left: 8px; }
 
/* ---- RESPONSIVE ---- */
@media (max-width: 1024px) {
  .cd-body { grid-template-columns: 1fr; }
  .cd-left-panel {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
}
 
@media only screen and (max-width: 767px) {
  .cd-left-panel { grid-template-columns: 1fr; }
  .cd-tab { padding: 7px 10px; font-size: 12px; }
  .cd-right-panel{
    overflow: hidden;
  }
}
.cd-right-panel{
  overflow-y: scroll;
}
</style>